'use client';

/**
 * Tenant Context Provider
 *
 * Provides tenant configuration and branding to all components.
 * Tenant is detected from hostname in middleware and passed via cookie.
 *
 * Usage:
 * - Wrap app with TenantProvider
 * - Use useTenant() hook to access tenant data
 * - Tenant branding applied via CSS variables
 */

import React, { createContext, useContext, useEffect, useLayoutEffect, useState } from 'react';
import { usePathname } from 'next/navigation';
import { applyTenantBranding } from './tenant-branding';
import { API_BASE } from './config';
import { RESERVED_SUBDOMAINS, isReservedSubdomain } from './reserved-subdomains';

// Use useLayoutEffect on client, useEffect on server (avoids SSR warnings)
const useIsomorphicLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect;

// Footer branding configuration
export interface FooterBranding {
  powered_by_vhire?: boolean;
  powered_by_style?: 'text' | 'badge' | 'logo';
  links?: Array<{ label: string; url: string }>;
  tagline?: string;
  company_name?: string;
  show_social_links?: boolean;
}

// Tenant branding configuration
export interface TenantBranding {
  logo_url?: string;
  logo_dark_url?: string;
  favicon_url?: string;
  primary_color?: string;
  secondary_color?: string;
  font_family?: string;
  tagline?: string;
  description?: string;
  hero_headline?: string;
  hero_description?: string;
  social_links?: {
    facebook?: string;
    twitter?: string;
    linkedin?: string;
    instagram?: string;
  };
  footer?: FooterBranding;
}

// Career page settings
export interface CareerPageSettings {
  title?: string;
  description?: string;
}

// Full tenant configuration
export interface TenantConfig {
  id: string;
  slug: string;
  name: string;
  company_name?: string;
  company_web_url?: string;
  description?: string;
  industry?: string;
  branding: TenantBranding;
  careerPage?: CareerPageSettings;
  isCustomDomain: boolean;
}

// Context value
interface TenantContextValue {
  tenant: TenantConfig | null;
  isLoading: boolean;
  isTenantDomain: boolean;
  error: string | null;
}

const TenantContext = createContext<TenantContextValue>({
  tenant: null,
  isLoading: true,
  isTenantDomain: false,
  error: null,
});

// Hook to access tenant context
export function useTenant() {
  const context = useContext(TenantContext);
  if (!context) {
    throw new Error('useTenant must be used within a TenantProvider');
  }
  return context;
}

/**
 * Reserved subdomain labels that must never be resolved as tenant slugs.
 *
 * When a user visits `tenant.vhire.com/admin` (a literal subdomain meant for
 * marketing, not a real tenant), the middleware can still drop a stale
 * `vhire_tenant=tenant` cookie. Without this guard the client-side fallback
 * inside TenantProvider hammers `/api/tenants/config?slug=tenant` — a 2-second,
 * always-404 call — every single page load. (See perf benchmark
 * 2026-05-21: tenant.vhire.com/admin had this exact 2s call returning 0 bytes.)
 *
 * Keep this list in sync with `RESERVED_SUBDOMAINS` in src/middleware.ts.
 * The middleware list is authoritative for hostname→slug parsing, but this
 * client-side guard catches the case where the cookie is set anyway (e.g.
 * the literal `tenant` subdomain that the middleware list doesn't cover, or
 * stale cookies from older middleware deploys).
 */
// Re-exported from the canonical source — kept under this name for backward
// compatibility with existing consumers (regression tests, etc.). The shared
// module guarantees this stays in sync with middleware.ts forever.
export const RESERVED_TENANT_SLUGS: ReadonlySet<string> = new Set(
  RESERVED_SUBDOMAINS,
);

/**
 * Pure helper — true when the given slug should NOT be resolved to a tenant.
 * Delegates to the canonical implementation in @/lib/reserved-subdomains.
 */
export function isReservedTenantSlug(slug: string | null | undefined): boolean {
  return isReservedSubdomain(slug);
}

// Get tenant slug from cookie (set by middleware)
function getTenantSlugFromCookie(): string | null {
  if (typeof document === 'undefined') return null;

  const cookies = document.cookie.split(';');
  for (const cookie of cookies) {
    const [name, value] = cookie.trim().split('=');
    if (name === 'vhire_tenant') {
      try {
        return decodeURIComponent(value || '');
      } catch {
        return null;
      }
    }
  }
  return null;
}

// Get tenant config from cookie (set by middleware - edge-resolved)
function getTenantConfigFromCookie(): TenantConfig | null {
  if (typeof document === 'undefined') return null;

  const cookies = document.cookie.split(';');
  for (const cookie of cookies) {
    const [name, ...valueParts] = cookie.trim().split('=');
    if (name === 'vhire_tenant_config') {
      try {
        const value = decodeURIComponent(valueParts.join('='));
        const data = JSON.parse(value);
        if (typeof data?.slug !== 'string' || !data.slug.trim()) return null;
        return {
          id: data.id,
          slug: data.slug,
          name: data.name,
          company_name: data.company_name,
          company_web_url: data.company_web_url,
          description: data.description || data.branding?.description,
          branding: data.branding || {},
          careerPage: data.careerPage || {},
          isCustomDomain: false, // Will be determined from slug cookie
        };
      } catch {
        return null;
      }
    }
  }
  return null;
}

// The slug cookie selects presentation only; never reuse a sibling tenant's
// cached config when middleware has selected a different host/slug.
function getCurrentTenantConfigFromCookie(): TenantConfig | null {
  const config = getTenantConfigFromCookie();
  const slug = getTenantSlugFromCookie();
  if (!config || !slug || isReservedTenantSlug(slug)) return null;
  if (slug.startsWith('custom:')) {
    return slug.slice(7).toLowerCase() === window.location.hostname.toLowerCase() ? config : null;
  }
  return config.slug.toLowerCase() === slug.toLowerCase() ? config : null;
}

interface TenantProviderProps {
  children: React.ReactNode;
}

export function TenantProvider({ children }: TenantProviderProps) {
  const pathname = usePathname();
  // Initialize state - will be null on server, updated synchronously on client
  const [tenant, setTenant] = useState<TenantConfig | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Brand the public career journey only. In particular, never override the
  // fixed-dark assessment/interview shell or the recruiter's product tokens.
  const isCareerRoute = pathname === '/' || pathname === '/jobs' || pathname?.startsWith('/jobs/') || pathname?.startsWith('/apply/');
  useIsomorphicLayoutEffect(() => {
    if (!tenant || !isCareerRoute) return;
    return applyTenantBranding(document.documentElement, tenant.branding.primary_color);
  }, [tenant, isCareerRoute]);

  // SYNCHRONOUS: Read cookie config immediately after hydration, before paint
  // This prevents any flash of incorrect content (FOIC)
  useIsomorphicLayoutEffect(() => {
    const cachedConfig = getCurrentTenantConfigFromCookie();
    const tenantSlug = getTenantSlugFromCookie();

    if (cachedConfig) {
      // Config was pre-fetched at the edge - use it immediately
      const isCustomDomain = tenantSlug?.startsWith('custom:') || false;
      cachedConfig.isCustomDomain = isCustomDomain;
      setTenant(cachedConfig);
      setIsLoading(false);

    } else if (!tenantSlug) {
      // No tenant - main vhire.com domain
      setIsLoading(false);
    }
    // If tenantSlug exists but no cached config, leave isLoading=true for async fetch
  }, []);

  // ASYNC: Fallback API fetch if cookie config not available
  useEffect(() => {
    const controller = new AbortController();
    async function loadTenant() {
      // Skip if we already have tenant from cookie (handled by layout effect above)
      if (tenant) return;

      try {
        const tenantSlug = getTenantSlugFromCookie();

        if (!tenantSlug) {
          // Already handled in layout effect
          return;
        }

        // Reserved-slug short-circuit. If the cookie still carries a label
        // like `tenant`, `admin`, `www`, etc. (e.g. a stale cookie from when
        // the middleware list didn't include it), don't burn a 2s API call
        // resolving it — it's the aggregator/marketing domain. Fall through
        // to the unbranded default. See perf benchmark 2026-05-21.
        if (isReservedTenantSlug(tenantSlug)) {
          setIsLoading(false);
          return;
        }

        // Check again in case layout effect set it
        const cachedConfig = getCurrentTenantConfigFromCookie();
        if (cachedConfig) {
          // Already handled in layout effect
          return;
        }

        // Fallback: Fetch from API if cookie config not available
        // This handles edge cases like middleware fetch failure
        const isCustomDomain = tenantSlug.startsWith('custom:');
        const lookupParam = isCustomDomain
          ? `domain=${encodeURIComponent(tenantSlug.replace('custom:', ''))}`
          : `slug=${encodeURIComponent(tenantSlug)}`;

        const response = await fetch(
          `${API_BASE}/tenants/config?${lookupParam}`,
          { signal: controller.signal },
        );
        if (controller.signal.aborted) return;

        if (!response.ok) {
          if (response.status === 404) {
            setError('Tenant not found');
          } else {
            setError('Failed to load tenant configuration');
          }
          setIsLoading(false);
          return;
        }

        const data = await response.json();
        if (controller.signal.aborted) return;

        if (data.success && data.data) {
          const tenantConfig: TenantConfig = {
            id: data.data.id,
            slug: data.data.slug,
            name: data.data.name,
            company_name: data.data.company_name,
            company_web_url: data.data.company_web_url,
            branding: data.data.branding || {},
            careerPage: data.data.careerPage || {},
            isCustomDomain,
          };

          setTenant(tenantConfig);

        }
      } catch (err) {
        if (!controller.signal.aborted) setError('Failed to load tenant');
      } finally {
        if (!controller.signal.aborted) setIsLoading(false);
      }
    }

    loadTenant();
    return () => controller.abort();
  }, [tenant]);

  const isTenantDomain = tenant !== null;

  return (
    <TenantContext.Provider value={{ tenant, isLoading, isTenantDomain, error }}>
      {children}
    </TenantContext.Provider>
  );
}
